import { useEffect, useState } from "react"
import { toast } from "react-toastify"
import { Card } from "~components/Card"
import PerkModal from "~components/perks/PerkModal"
import PerksList from "~components/perks/PerksList"
import PageContent from "~components/PageContent"
import TablePaginator from "~components/TablePaginator"
import PerkRequestModal from "~components/perks/PerkRequestModal"
import { ButtonPrimarySoftLarge } from "~styled/button"
import { RBAC_RULES } from "~rbac/constants/rbac-rules"
import IsAuthorized from "~rbac/isAuthorized"
import PerkService from "~services/PerkService"
import { PerkShape, PostPerkShape } from "~shapes/perk"
import { OnRequestCloseProps } from "~shapes/modal"
import { paginatorInitState, TablePaginatorShape } from "~shapes/api-table"
import { ContainerSpaceBetween } from "~styled/containers"
import TableSearchField from "~components/TableSearchField"
import PageHeader from "~components/PageHeader"
import usePerks from "~hooks/usePerks"
import { useDeepCompareEffectNoCheck } from "use-deep-compare-effect"
import { useMutation } from "react-query"
import { GetCompanyOptionsShape } from "~shapes/company"

export enum ACTIONS {
  MENU,
  REDEEM,
}

const Perks = () => {
  const [perk, setPerk] = useState<PerkShape | undefined>(undefined)
  const [perks, setPerks] = useState<PerkShape[]>([])
  const [paginator, setPaginator] = useState<TablePaginatorShape>(
    paginatorInitState
  )
  const tableOptions = { sort: "name", size: 15 }
  const [isPerkModalOpened, setIsPerkModalOpened] = useState<boolean>(false)
  const [options, setOptions] = useState<GetCompanyOptionsShape | undefined>(
    undefined
  )

  const {
    data: perksData,
    refetch: fetchPerks,
    isFetching: isLoadingPerks,
    isError: isErrorFetchingPerks,
  } = usePerks({ ...tableOptions, ...options })

  const {
    mutateAsync: mutatePerk,
    isError: errorUpdatingPerk,
  } = useMutation("perk", (payload: Partial<PostPerkShape>) =>
    PerkService.patchPerk(payload)
  )

  useEffect(() => {
    ;(async () => {
      await fetchPerks()
    })()
  }, [options])

  useDeepCompareEffectNoCheck(() => {
    if (perksData) {
      setPaginator(perksData.pages)
      setPerks(perksData.result)
    }
  }, [perksData])

  useEffect(() => {
    if (isErrorFetchingPerks) {
      toast.error(`Failed to load the perks`)
      setPaginator(paginatorInitState)
      setPerks([])
    }
    if (errorUpdatingPerk) toast.error(`Failed to update the perk status`)
  }, [isErrorFetchingPerks, errorUpdatingPerk])

  const handlePageChange = (page: number) => {
    setOptions({ ...options, ...{ page: page } })
  }

  const handleEditPerk = (perk: PerkShape) => {
    setPerk(perk)
    setIsPerkModalOpened(true)
  }

  const handleOpenPerkModal = () => {
    setPerk(undefined)
    setIsPerkModalOpened(true)
  }

  const handleOnRequestClose = ({ shouldRefreshList }: OnRequestCloseProps) => {
    if (shouldRefreshList)
      setOptions({ ...options, ...{ page: paginator.number } })
    setIsPerkModalOpened(false)
  }

  // Method to toggle the perk status
  const handlePerkStatusChange = async (data: Partial<PostPerkShape>) => {
    const response = await mutatePerk(data)
    const updatedPerks = perks.map(perk => {
      if (perk.id !== response.id) return perk
      return { ...perk, status: response.status }
    })
    setPerks(updatedPerks)
  }

  return (
    <PageContent>
      <PageHeader title={"Perks"} />
      <Card>
        <ContainerSpaceBetween style={{ marginBottom: 30 }}>
          <TableSearchField
            searchBy={["company.name"]}
            placeholder="SEARCH BY COMPANY NAME"
            callback={el => setOptions(el)}
          />

          <ButtonPrimarySoftLarge onClick={handleOpenPerkModal}>
            Create a new perk
          </ButtonPrimarySoftLarge>
        </ContainerSpaceBetween>

        <PerksList
          perks={perks}
          isLoading={isLoadingPerks}
          onClickPerkEdit={handleEditPerk}
          onChangeStatus={handlePerkStatusChange}
          action={ACTIONS.MENU}
        />
        {paginator?.totalElements > 0 && (
          <TablePaginator page={paginator} onPageChange={handlePageChange} />
        )}
      </Card>

      {isPerkModalOpened && (
        <>
          <IsAuthorized action={RBAC_RULES.CREATE_PERK}>
            <PerkModal perk={perk} onRequestClose={handleOnRequestClose} />
          </IsAuthorized>

          <IsAuthorized action={RBAC_RULES.CREATE_PERK_REQUEST}>
            <PerkRequestModal
              perk={perk}
              onRequestClose={handleOnRequestClose}
            />
          </IsAuthorized>
        </>
      )}
    </PageContent>
  )
}

export default Perks
